import * as React from "react";
import { useTranslation } from "react-i18next";

import { MenuAction } from "./AllMcsView";
import ExportFileView from "components/export/ExportFileView";
import FailedView from "components/import-file-dialog/FailedView";
import style from "components/import-file-dialog/import-file-dialog.scss";
import ImportFileDialog, { DialogState } from "components/import-file-dialog/ImportFileDialog";
import LoadingView from "components/import-file-dialog/LoadingView";
import SelectingView from "components/import-file-dialog/SelectingView";
import { hybridMcService, OfflineResponse } from "services/hybrid-mc/HybridMcService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";

interface Props {
    onClose: () => void;
    setModalTitle: (title: string) => void;
    action: MenuAction;
}

interface OfflineConfiguration {
    uploadSuccessTitle: string;
    downloadMessage: string;
    downloadFileName: string;
    userStatisticsAction: string;
    defaultTitle: string;
}

const offlineActionsConfiguration: Record<MenuAction, OfflineConfiguration> = {
    REGISTER: {
        uploadSuccessTitle: "Mcs.offline.downloadFile.downloadResponseFile",
        downloadMessage: "Mcs.offline.downloadFile.downloadRegistrationFile",
        downloadFileName: "activation_complete_bundle",
        userStatisticsAction: Action.REGISTER_OFFLINE,
        defaultTitle: "Mcs.offline.registerAndSyncButton",
    },
    SYNC: {
        uploadSuccessTitle: "Mcs.offline.downloadFile.downloadSyncResponseFile",
        downloadMessage: "Mcs.offline.downloadFile.downloadSyncFile",
        downloadFileName: "sync_complete_bundle",
        userStatisticsAction: Action.SYNC_OFFLINE,
        defaultTitle: "Mcs.offline.syncButton",
    },
};

const ImportOfflineHybridFileDialog = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const [dialogState, setDialogState] = React.useState(DialogState.SELECTING_FILE);
    const abortControllerRef = React.useRef<AbortController | null>(null);
    const [error, setError] = React.useState<{ field: string; message: string } | string | undefined>(undefined);
    const [registrationResponse, setRegistrationResponse] = React.useState<OfflineResponse>();
    const selectedFileListReference = React.useRef<File[] | undefined>(undefined);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const offlineActionConfiguration = offlineActionsConfiguration[props.action];

    React.useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const dialogLogic = () => {
        switch (dialogState) {
            case DialogState.SELECTING_FILE:
                setError(undefined);
                return;
            case DialogState.LOADING_FILE: {
                if (selectedFileListReference.current === undefined) {
                    setError(t("Common.noFileSelected"));
                    return;
                }

                const reader = new FileReader();
                reader.readAsText(selectedFileListReference.current[0]);

                reader.onload = async () => {
                    try {
                        if (typeof reader.result === "string") {
                            const jsonData = JSON.parse(reader.result);

                            setDialogState(DialogState.IMPORTING_FILE);

                            const abortController = new AbortController();
                            abortControllerRef.current = abortController;

                            let response: OfflineResponse;

                            try {
                                if (props.action === "REGISTER") {
                                    response = await hybridMcService.registerOffline(abortController, jsonData);
                                } else {
                                    response = await hybridMcService.syncOffline(abortController, jsonData);
                                }

                                setRegistrationResponse(response);
                                setDialogState(DialogState.IMPORTING_SUCCEEDED);
                            } catch {
                                setError(t("Common.failedToUploadFile"));
                                setDialogState(DialogState.IMPORTING_FAILED);
                            }
                        }
                    } catch {
                        setError(t("Common.fileParseError"));
                        setDialogState(DialogState.LOADING_FILE_FAILED);
                    }
                };
                return;
            }
            case DialogState.IMPORTING_SUCCEEDED:
                props.setModalTitle(t(offlineActionConfiguration.uploadSuccessTitle));
                setError(undefined);
                return;
        }
    };

    const dispatch = () => {
        if (selectedFileListReference.current === undefined || selectedFileListReference.current?.length === 0) {
            setDialogState(DialogState.SELECTING_FILE);
        } else if (selectedFileListReference.current?.length === 1) {
            setDialogState(DialogState.LOADING_FILE);
        } else {
            setDialogState(DialogState.LOADING_FILES);
        }
    };

    const addUserStatistics = () => {
        usageStatisticsService.sendEvent({
            category: Category.MCS,
            action: offlineActionConfiguration.userStatisticsAction,
        });
    };

    const handleFileDrop = (fileList: FileList, event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        addUserStatistics();
        if (fileList.length > 0) {
            selectedFileListReference.current = Array.from(fileList);
        }
        dispatch();
    };

    const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (files !== null) {
            selectedFileListReference.current = Array.from(files);
        }
        dispatch();
    };

    const onSelectFileClicked = () => {
        addUserStatistics();
        fileInputRef.current?.click();
    };

    const reset = () => {
        selectedFileListReference.current = undefined;
        props.setModalTitle(t(offlineActionConfiguration.defaultTitle));
        setError(undefined);
        setDialogState(DialogState.SELECTING_FILE);
    };

    const handleDownload = () => {
        const blob = new Blob([JSON.stringify(registrationResponse, null, 2)], {
            type: "application/octet-stream",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = offlineActionConfiguration.downloadFileName;
        a.click();
        URL.revokeObjectURL(url);
    };

    const selecting = (
        <SelectingView
            handleFileDrop={handleFileDrop}
            onFileInputChange={onFileInputChange}
            onSelectFileClicked={onSelectFileClicked}
            fileInputRef={fileInputRef}
        />
    );
    const loadingFile = <LoadingView loadingMessage={t("Common.uploading")} />;
    const loadingFiles = (
        <FailedView
            failureMessage={t("Common.multipleFilesError")}
            onUploadAnotherClicked={reset}
            onClose={props.onClose}
        />
    );
    const loadingFailed = (
        <FailedView
            failureMessage={typeof error === "string" ? error : t("Common.failedToLoadFile")}
            onUploadAnotherClicked={reset}
            onClose={props.onClose}
        />
    );
    const importing = <LoadingView loadingMessage={t("Common.uploading")} />;

    const importingSucceeded = (
        <ExportFileView
            downloadMessage={t(offlineActionConfiguration.downloadMessage)}
            onDownloadClicked={handleDownload}
        />
    );

    const importingFailedErrorMessage = <div className={style.introductionLabel}>{t("Common.failedToUploadFile")}</div>;

    const importingFailed = (
        <FailedView
            failureMessage={importingFailedErrorMessage}
            onUploadAnotherClicked={reset}
            onClose={props.onClose}
        />
    );

    const stateToContent = new Map<DialogState, JSX.Element>([
        [DialogState.SELECTING_FILE, selecting],
        [DialogState.LOADING_FILE, loadingFile],
        [DialogState.LOADING_FILES, loadingFiles],
        [DialogState.LOADING_FILE_FAILED, loadingFailed],
        [DialogState.IMPORTING_FILE, importing],
        [DialogState.IMPORTING_SUCCEEDED, importingSucceeded],
        [DialogState.IMPORTING_FAILED, importingFailed],
    ]);

    return (
        <ImportFileDialog
            onClose={props.onClose}
            dialogLogic={dialogLogic}
            stateToContent={stateToContent}
            stateDispatch={dispatch}
            dialogState={dialogState}
        />
    );
};

export default ImportOfflineHybridFileDialog;
